// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * An archive's positions as the BOM tree they came from — shared by the archive page and
 * the change dialog, which both list the whole machine and fold it by level.
 */

import type { ArchiveItem } from "@/api/types";
import type { FoldNode } from "@/tree/fold";

/**
 * The positions in the order the BOM lists their lines (its `sort_path`), slots of one
 * line in slot order; positions with no BOM line — free-form, or added by a change — after
 * them, by code, as they were listed before.
 */
export function inBomOrder(items: ArchiveItem[]): ArchiveItem[] {
  const keyOf = (item: ArchiveItem) => item.bom_sort_path ?? `\uffff${item.position_code}`;
  return [...items].sort((a, b) => {
    const byPath = keyOf(a) < keyOf(b) ? -1 : keyOf(a) > keyOf(b) ? 1 : 0;
    return byPath || (a.slot_no ?? 0) - (b.slot_no ?? 0);
  });
}

/**
 * The positions as a tree. A position sits under its BOM line's parent line — under that
 * line's position, or, when the parent is a critical line expanded into slots, under its
 * last slot, which is the row directly above it in BOM order.
 */
export function foldNodes(items: ArchiveItem[]): FoldNode[] {
  const rowOfLine = new Map<string, string>();
  for (const item of items) {
    if (item.bom_item_id) rowOfLine.set(item.bom_item_id, item.position_id);
  }
  return items.map((item) => ({
    key: item.position_id,
    parentKey: item.bom_parent_item_id ? (rowOfLine.get(item.bom_parent_item_id) ?? null) : null,
    depth: item.bom_sort_path ? item.bom_sort_path.split(".").length - 1 : 0,
  }));
}
